import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  FiArrowLeft, FiCheck, FiXCircle, FiArchive, 
  FiFileText, FiPenTool, FiLayers, FiPlay, 
  FiAlertTriangle, FiDownload, FiExternalLink,
  FiDollarSign, FiTrendingUp, FiClock, FiBarChart2
} from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { mandateAPI, pdfAPI, reportAPI } from '../../services/api';

// ── Signature Canvas Draw Pad ────────────────────────────────────────────────
const SignaturePad = ({ onSave, onClose, title }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1e3a5f';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    
    if (e.touches && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    const isEmpty = !canvas.getContext('2d')
      .getImageData(0, 0, canvas.width, canvas.height)
      .data.some(channel => channel !== 0);
    
    if (isEmpty) {
      alert('Veuillez dessiner votre signature avant de sauvegarder.');
      return;
    }
    
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
      background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100
    }}>
      <div className="card" style={{ width: '100%', maxWidth: 450, margin: 16, background: '#fff', borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
        <div className="card-header" style={{ borderBottom: '1px solid var(--border)', padding: '16px 20px' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h2>
        </div>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '20px' }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
            Utilisez votre souris ou votre doigt pour signer dans le cadre ci-dessous.
          </p>
          <canvas
            ref={canvasRef}
            width={400}
            height={200}
            style={{
              border: '2px dashed #cbd5e1',
              borderRadius: 8,
              background: '#f8fafc',
              cursor: 'crosshair',
              touchAction: 'none',
              width: '100%',
              maxWidth: 400,
              height: 200
            }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 8 }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={clear}>Effacer</button>
            <div style={{ display: 'flex', gap: 12 }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Annuler</button>
              <button type="button" className="btn btn-primary btn-sm" onClick={handleSave}>Enregistrer</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Detail Page Component ───────────────────────────────────────────────────
const MandateDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mandate, setMandate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signingClient, setSigningClient] = useState(false);
  const [signingAgency, setSigningAgency] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [activating, setActivating] = useState(false);
  const [terminating, setTerminating] = useState(false);
  const [showTermModal, setShowTermModal] = useState(false);
  const [termForm, setTermForm] = useState({ reason: '', status: 'cancelled' });

  // État financier
  const [activeTab, setActiveTab]         = useState('details');
  const [financial, setFinancial]         = useState(null);
  const [financialLoading, setFinancialLoading] = useState(false);

  // Signatures pad triggers
  const [showClientSigPad, setShowClientSigPad] = useState(false);
  const [showAgencySigPad, setShowAgencySigPad] = useState(false);

  // Termes & Conditions du mandat
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsForm, setTermsForm] = useState('');
  const [termsSaving, setTermsSaving] = useState(false);

  const handleSaveTerms = async (e) => {
    e.preventDefault();
    setTermsSaving(true);
    try {
      await mandateAPI.update(mandate.id, { terms: termsForm });
      setMandate(prev => ({ ...prev, terms: termsForm }));
      setShowTermsModal(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la mise à jour des termes du mandat');
    } finally {
      setTermsSaving(false);
    }
  };

  const fetchMandate = () => {
    setLoading(true);
    mandateAPI.getById(id)
      .then(r => { 
        setMandate(r.data); 
        setLoading(false); 
      })
      .catch(() => navigate('/mandates'));
  };

  useEffect(() => {
    fetchMandate();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'financial' && id && !financial) {
      setFinancialLoading(true);
      reportAPI.getMandateFinancial(id)
        .then(r => setFinancial(r.data))
        .catch(() => {})
        .finally(() => setFinancialLoading(false));
    }
  }, [activeTab, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGeneratePdf = async () => {
    setGeneratingPdf(true);
    try {
      const pdfUrl = `/uploads/mandates/MND_${mandate.mandate_number || id}.pdf`;
      await mandateAPI.generatePdf(id, { pdf_url: pdfUrl });
      fetchMandate();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la génération du contrat PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleSignClientSave = async (dataUrl) => {
    setSigningClient(true);
    setShowClientSigPad(false);
    try {
      await mandateAPI.signClient(id, { signature_url: dataUrl });
      fetchMandate();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la signature client');
    } finally {
      setSigningClient(false);
    }
  };

  const handleSignAgencySave = async (dataUrl) => {
    setSigningAgency(true);
    setShowAgencySigPad(false);
    try {
      await mandateAPI.signAgency(id, { signature_url: dataUrl });
      fetchMandate();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la signature agence');
    } finally {
      setSigningAgency(false);
    }
  };

  const handleActivate = async () => {
    setActivating(true);
    try {
      await mandateAPI.activate(id);
      fetchMandate();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de l\'activation');
    } finally {
      setActivating(false);
    }
  };

  const handleTerminateSubmit = async (e) => {
    e.preventDefault();
    setTerminating(true);
    try {
      await mandateAPI.terminate(id, termForm);
      setShowTermModal(false);
      fetchMandate();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la clôture');
    } finally {
      setTerminating(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await pdfAPI.downloadMandate(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mandat-${mandate.mandate_number || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      alert('Erreur lors du téléchargement du PDF');
    }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!mandate) return null;

  const typeLabels = { sale: 'Vente', rental: 'Location', management: 'Gestion' };
  
  const steps = [
    { key: 'draft', label: '1. Brouillon', desc: 'Mandat en cours de rédaction' },
    { key: 'pending_signature', label: '2. Génération PDF', desc: 'Contrat PDF en attente de signatures' },
    { key: 'signed', label: '3. Signatures complètes', desc: 'Signé par les deux parties' },
    { key: 'active', label: '4. Actif', desc: 'Mandat actif, bien géré' },
  ];

  const getStepStatus = (stepKey) => {
    const sequence = ['draft', 'pending_signature', 'signed', 'active'];
    const currentIdx = sequence.indexOf(mandate.status);
    const stepIdx = sequence.indexOf(stepKey);

    if (mandate.status === 'cancelled' || mandate.status === 'expired') {
      return 'terminated';
    }

    if (currentIdx === stepIdx) return 'current';
    if (currentIdx > stepIdx) return 'completed';
    return 'upcoming';
  };

  const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';

  const STATUS_PAYMENT = {
    paid:          { label: 'Payé',        cls: 'badge-success' },
    pending:       { label: 'En attente',   cls: 'badge-warning' },
    overdue:       { label: 'En retard',    cls: 'badge-danger' },
    partially_paid:{ label: 'Partiel',      cls: 'badge-info' },
  };

  return (
    <div style={{ paddingBottom: 40 }}>
      {/* Page Header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/mandates')} title="Retour à la liste">
            <FiArrowLeft />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Mandat {mandate.mandate_number}</h1>
              <span className={`badge badge-${
                mandate.status === 'active' ? 'success' : 
                mandate.status === 'signed' ? 'info' : 
                ['cancelled', 'expired'].includes(mandate.status) ? 'danger' : 'warning'
              }`} style={{ padding: '4px 10px', fontSize: 12 }}>
                {mandate.status.toUpperCase()}
              </span>
            </div>
            <p style={{ margin: '4px 0 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
              Créé le {new Date(mandate.created_at).toLocaleDateString('fr-FR')} par {mandate.created_by_first} {mandate.created_by_last}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          {['active', 'signed', 'pending_signature', 'draft'].includes(mandate.status) && (
            <button className="btn btn-danger btn-sm" onClick={() => setShowTermModal(true)}>
              <FiXCircle /> Clôturer / Annuler
            </button>
          )}
        </div>
      </div>

      {/* Navigation par onglets */}
      <div className="tabs" style={{ marginBottom: 24 }}>
        {[
          { key: 'details',   label: '📋 Détails & Workflow' },
          { key: 'financial', label: '💰 État financier' },
        ].map(t => (
          <button
            key={t.key}
            className={`tab ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ ONGLET DÉTAILS & WORKFLOW ══════════════════════════════════ */}
      {activeTab === 'details' && (
        <>

      {/* Barre visuelle premium de progression du cycle de vie */}
      <div className="card" style={{ marginBottom: 24, borderLeft: '4px solid var(--primary)', background: 'var(--card-bg)' }}>
        <div className="card-body" style={{ padding: '24px 20px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: 15, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
            Cycle de vie du mandat
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
            {steps.map((step) => {
              const status = getStepStatus(step.key);
              let bgColor = '#f3f4f6';
              let textColor = '#4b5563';
              let borderColor = '#e5e7eb';

              if (status === 'current') {
                bgColor = 'var(--primary-light || #7c3aed22)';
                textColor = 'var(--primary || #7c3aed)';
                borderColor = 'var(--primary || #7c3aed)';
              } else if (status === 'completed') {
                bgColor = '#10b98115';
                textColor = '#10b981';
                borderColor = '#10b98155';
              } else if (status === 'terminated') {
                bgColor = '#ef444415';
                textColor = '#ef4444';
                borderColor = '#ef444455';
              }

              return (
                <div key={step.key} style={{
                  padding: 16,
                  borderRadius: 8,
                  background: bgColor,
                  border: `1px solid ${borderColor}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  transition: 'all 0.2s ease',
                }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: textColor }}>
                    {step.label} {status === 'completed' && '✓'}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    {step.desc}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Boîte d'actions contextuelles basées sur l'état */}
      <div className="card" style={{ marginBottom: 24, background: 'var(--card-bg)' }}>
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiLayers style={{ color: 'var(--primary)' }} />
          <h2 style={{ margin: 0, fontSize: 16 }}>Actions requises pour l'étape suivante</h2>
        </div>
        <div className="card-body">
          {mandate.status === 'draft' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ margin: 0, fontSize: 14 }}>
                Le mandat est actuellement en statut de <strong>Brouillon</strong>. 
                Veuillez générer le contrat de mandat au format PDF officiel pour passer à l'étape des signatures.
              </p>
              <div>
                <button className="btn btn-primary" onClick={handleGeneratePdf} disabled={generatingPdf}>
                  <FiFileText style={{ marginRight: 8 }} />
                  {generatingPdf ? 'Génération du contrat PDF...' : 'Générer le contrat (PDF)'}
                </button>
              </div>
            </div>
          )}

          {mandate.status === 'pending_signature' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <p style={{ margin: 0, fontSize: 14 }}>
                Le contrat a été généré avec succès. Les signatures du client (propriétaire) et de l'agence sont requises.
              </p>
              
              <div style={{ background: '#f3f4f6', padding: 12, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FiFileText style={{ color: 'red' }} /> Contrat de mandat avec signatures en cours
                </span>
                <button onClick={handleDownloadPdf} className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <FiDownload /> Visualiser / Télécharger le PDF
                </button>
              </div>

              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
                <div style={{ flex: 1, minWidth: 200, padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 600 }}>Signature du Propriétaire</h4>
                  {mandate.client_signed_at ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                        <FiCheck /> Signé le {new Date(mandate.client_signed_at).toLocaleDateString('fr-FR')}
                      </div>
                      {mandate.client_signature_url && (
                        <img src={mandate.client_signature_url} alt="Signature Client" style={{ maxHeight: 60, maxWidth: 150, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 4, padding: 4 }} />
                      )}
                    </div>
                  ) : (
                    <div>
                      <button className="btn btn-primary btn-sm" onClick={() => setShowClientSigPad(true)} disabled={signingClient}>
                        <FiPenTool style={{ marginRight: 6 }} />
                        {signingClient ? 'Enregistrement...' : 'Dessiner la signature'}
                      </button>
                    </div>
                  )}
                </div>

                <div style={{ flex: 1, minWidth: 200, padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 600 }}>Signature de l'Agence</h4>
                  {mandate.agency_signed_at ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                        <FiCheck /> Signé le {new Date(mandate.agency_signed_at).toLocaleDateString('fr-FR')}
                      </div>
                      {mandate.agency_signature_url && (
                        <img src={mandate.agency_signature_url} alt="Signature Agence" style={{ maxHeight: 60, maxWidth: 150, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 4, padding: 4 }} />
                      )}
                    </div>
                  ) : (
                    <div>
                      <button className="btn btn-primary btn-sm" onClick={() => setShowAgencySigPad(true)} disabled={signingAgency}>
                        <FiPenTool style={{ marginRight: 6 }} />
                        {signingAgency ? 'Enregistrement...' : 'Dessiner la signature'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {mandate.status === 'signed' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <p style={{ margin: 0, fontSize: 14 }}>
                Félicitations ! Le mandat est entièrement signé par le propriétaire et l'agence. 
                Téléchargez le document signé final ou activez-le officiellement pour démarrer les opérations de publication.
              </p>
              
              <div style={{ background: '#f3f4f6', padding: 12, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FiFileText style={{ color: 'red' }} /> Contrat signé final (PDF)
                </span>
                <button onClick={handleDownloadPdf} className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <FiDownload /> Télécharger le PDF signé
                </button>
              </div>

              <div>
                <button className="btn btn-success" onClick={handleActivate} disabled={activating}>
                  <FiPlay style={{ marginRight: 8 }} />
                  {activating ? 'Activation en cours...' : 'Activer le mandat'}
                </button>
              </div>
            </div>
          )}

          {mandate.status === 'active' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <span style={{
                  background: '#10b98122', color: '#10b981', padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600
                }}>
                  Mandat actif et valide ✓
                </span>
                <button onClick={handleDownloadPdf} className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <FiDownload /> Télécharger le document PDF officiel
                </button>
              </div>
              <p style={{ margin: '0', fontSize: 14 }}>
                Le mandat est opérationnel. Vous pouvez maintenant gérer les annonces de ce bien immobilier et lancer les actions marketing.
              </p>
              <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                <Link to="/announcements" className="btn btn-primary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <FiExternalLink /> Gérer les publications
                </Link>
                {mandate.property_id && (
                  <Link to={`/properties/${mandate.property_id}`} className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <FiLayers /> Voir la fiche du bien
                  </Link>
                )}
              </div>
            </div>
          )}

          {['cancelled', 'expired'].includes(mandate.status) && (
            <div style={{ background: '#ef44440c', border: '1px solid #ef444433', padding: 16, borderRadius: 6 }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#ef4444', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FiAlertTriangle /> Mandat clôturé / inactif
              </h4>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                Ce mandat n'est plus en vigueur. Statut actuel : <strong>{mandate.status}</strong>.
              </p>
              {mandate.termination_reason && (
                <p style={{ margin: '8px 0 0 0', fontSize: 13, background: '#fff', padding: 10, borderRadius: 4, borderLeft: '3px solid #ef4444' }}>
                  <strong>Motif :</strong> {mandate.termination_reason}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Informations Générales */}
      <div className="grid-2">
        <div className="card" style={{ background: 'var(--card-bg)' }}>
          <div className="card-header"><h2 style={{ fontSize: 16 }}>Détails juridiques et administratifs</h2></div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="detail-row">
              <div className="detail-label">Type de mandat</div>
              <div className="detail-value" style={{ fontWeight: 600 }}>{typeLabels[mandate.type] || mandate.type}</div>
            </div>
            <div className="detail-row">
              <div className="detail-label">Client (Propriétaire)</div>
              <div className="detail-value">
                {mandate.client_id ? (
                  <Link to={`/clients/${mandate.client_id}`} style={{ fontWeight: 600, color: 'var(--primary)', textDecoration: 'none' }}>
                    👤 {mandate.client_first_name} {mandate.client_last_name}
                  </Link>
                ) : 'Non assigné'}
              </div>
            </div>
            {mandate.client_email && (
              <div className="detail-row">
                <div className="detail-label">Contact Client</div>
                <div className="detail-value">{mandate.client_email} {mandate.client_phone ? `| ${mandate.client_phone}` : ''}</div>
              </div>
            )}
            <div className="detail-row">
              <div className="detail-label">Bien concerné</div>
              <div className="detail-value">
                {mandate.property_id ? (
                  <Link to={`/properties/${mandate.property_id}`} style={{ fontWeight: 600, color: 'var(--primary)', textDecoration: 'none' }}>
                    🏢 {mandate.property_title || 'Fiche du bien'}
                  </Link>
                ) : 'Aucun bien associé'}
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-label">Taux de Commission</div>
              <div className="detail-value" style={{ color: 'var(--primary)', fontWeight: 700 }}>
                {mandate.commission_rate ? `${mandate.commission_rate}%` : 'Aucune'}
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-label">Régime d'exclusivité</div>
              <div className="detail-value">
                {mandate.exclusive ? (
                  <span className="badge badge-warning">Mandat Exclusif</span>
                ) : 'Simple / Non exclusif'}
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-label">Période de validité</div>
              <div className="detail-value">
                Du <strong>{new Date(mandate.start_date).toLocaleDateString('fr-FR')}</strong> au <strong>{mandate.end_date ? new Date(mandate.end_date).toLocaleDateString('fr-FR') : 'Indéterminée'}</strong>
              </div>
            </div>
          </div>
        </div>

        {/* Termes, Conditions & Clauses du Mandat */}
        <div className="card" style={{ background: 'var(--card-bg)', marginBottom: 20 }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
              <FiFileText color="#2563eb" /> Termes, Conditions & Clauses Particulières du Mandat
            </h2>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                setTermsForm(mandate.terms || '');
                setShowTermsModal(true);
              }}
              style={{ fontSize: 12, padding: '4px 10px' }}
            >
              Modifier les termes
            </button>
          </div>
          <div className="card-body">
            {mandate.terms ? (
              <p style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 13, lineHeight: '1.6', color: 'var(--text-primary)' }}>
                {mandate.terms}
              </p>
            ) : (
              <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: 13, fontStyle: 'italic' }}>
                Aucune clause ou condition spécifique définie pour ce mandat. Cliquez sur « Modifier les termes » pour en saisir.
              </p>
            )}
          </div>
        </div>

        <div className="card" style={{ background: 'var(--card-bg)' }}>
          <div className="card-header"><h2 style={{ fontSize: 16 }}>Notes & Informations complémentaires</h2></div>
          <div className="card-body">
            {mandate.notes ? (
              <p style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 13, lineHeight: '1.6', color: 'var(--text-secondary)' }}>
                {mandate.notes}
              </p>
            ) : (
              <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: 13 }}>
                Aucune note ou clause additionnelle enregistrée pour ce mandat.
              </p>
            )}
          </div>
        </div>
      </div>

        </>
      )}

      {/* ════ Modals — toujours visibles quel que soit l'onglet ════ */}
      {showClientSigPad && (
        <SignaturePad
          title="Signature du Client (Propriétaire)"
          onClose={() => setShowClientSigPad(false)}
          onSave={handleSignClientSave}
        />
      )}

      {showAgencySigPad && (
        <SignaturePad
          title="Signature de l'Agence"
          onClose={() => setShowAgencySigPad(false)}
          onSave={handleSignAgencySave}
        />
      )}

      {/* Modal d'édition des termes du mandat */}
      {showTermsModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ width: '100%', maxWidth: 600, margin: 16, background: '#fff', borderRadius: 10 }}>
            <div className="card-header">
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Modifier les termes & clauses du mandat</h2>
            </div>
            <form onSubmit={handleSaveTerms}>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '20px' }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
                  Termes, conditions et obligations particulières liant l'agence et le propriétaire
                </label>
                <textarea
                  className="form-control"
                  rows={8}
                  value={termsForm}
                  onChange={e => setTermsForm(e.target.value)}
                  placeholder="Ex: Modalités de reddition des comptes, autorisations de travaux d'urgence jusqu'à un certain montant, exclusivité publicitaire, préavis de résiliation..."
                  style={{ lineHeight: '1.5', fontFamily: 'inherit' }}
                />
                <small style={{ color: 'var(--text-secondary)' }}>
                  Ces conditions figureront dans le contrat officiel de mandat PDF généré pour le propriétaire.
                </small>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 20px', borderTop: '1px solid var(--border)' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowTermsModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary" disabled={termsSaving}>
                  {termsSaving ? 'Enregistrement...' : 'Enregistrer les termes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de résiliation/annulation */}
      {showTermModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ width: '100%', maxWidth: 450, margin: 16, background: '#fff' }}>
            <div className="card-header"><h2>Clôturer le mandat</h2></div>
            <form onSubmit={handleTerminateSubmit}>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="form-group">
                  <label>Type de clôture</label>
                  <select className="form-control" value={termForm.status} onChange={e => setTermForm({ ...termForm, status: e.target.value })}>
                    <option value="cancelled">Annulation / Résiliation anticipée</option>
                    <option value="expired">Expiration naturelle du mandat</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Raison de la clôture *</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    required
                    value={termForm.reason}
                    onChange={e => setTermForm({ ...termForm, reason: e.target.value })}
                    placeholder="Veuillez spécifier le motif précis..."
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 20px', borderTop: '1px solid var(--border)' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowTermModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-danger" disabled={terminating}>
                  {terminating ? 'Traitement...' : 'Confirmer la clôture'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══ ONGLET ÉTAT FINANCIER ══════════════════════════════════════ */}
      {activeTab === 'financial' && (
        <div style={{ marginTop: 24 }}>
          {financialLoading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : !financial ? (
            <div className="card"><div className="card-body"><p>Aucune donnée financière disponible.</p></div></div>
          ) : (
            <>
              {/* KPIs */}
              <div className="grid-4" style={{ marginBottom: 24 }}>
                {[
                  { icon: <FiDollarSign />, label: 'Loyers collectés', value: fmt(financial.summary.total_collected) + ' XOF', color: '#22c55e', bg: '#dcfce7' },
                  { icon: <FiTrendingUp />, label: `Commission agence (${financial.summary.commission_rate}%)`, value: fmt(financial.summary.agency_commission) + ' XOF', color: '#3b82f6', bg: '#dbeafe' },
                  { icon: <FiBarChart2 />, label: 'Net propriétaire', value: fmt(financial.summary.owner_net) + ' XOF', color: '#7c3aed', bg: '#ede9fe' },
                  { icon: <FiClock />, label: 'Loyers en attente', value: fmt(financial.summary.total_pending) + ' XOF', color: financial.summary.total_overdue > 0 ? '#ef4444' : '#f59e0b', bg: financial.summary.total_overdue > 0 ? '#fee2e2' : '#fffbeb' },
                ].map((kpi, i) => (
                  <div key={i} className="card">
                    <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: kpi.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: kpi.color, fontSize: 20, flexShrink: 0 }}>
                        {kpi.icon}
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 2 }}>{kpi.label}</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Stats secondaires */}
              <div className="grid-4" style={{ marginBottom: 24 }}>
                <div className="card"><div className="card-body" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#22c55e' }}>{financial.summary.count_paid}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Paiements reçus</div>
                </div></div>
                <div className="card"><div className="card-body" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#f59e0b' }}>{financial.summary.count_pending}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>En attente</div>
                </div></div>
                <div className="card"><div className="card-body" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#ef4444' }}>{financial.summary.count_overdue}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>En retard</div>
                </div></div>
                <div className="card"><div className="card-body" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#0ea5e9' }}>{financial.summary.contracts_count}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Contrat(s) de bail</div>
                </div></div>
              </div>

              {/* Graphique mensuel */}
              {financial.monthly_breakdown.length > 0 && (
                <div className="card" style={{ marginBottom: 24 }}>
                  <div className="card-header"><h2><FiBarChart2 /> Évolution mensuelle (12 mois)</h2></div>
                  <div className="card-body" style={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={[...financial.monthly_breakdown].reverse()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={v => (v/1000).toFixed(0) + 'k'} />
                        <Tooltip formatter={v => `${parseInt(v).toLocaleString('fr-FR')} XOF`} />
                        <Legend />
                        <Bar dataKey="collected" name="Collecté" fill="#22c55e" radius={[4,4,0,0]} />
                        <Bar dataKey="commission" name="Commission agence" fill="#3b82f6" radius={[4,4,0,0]} />
                        <Bar dataKey="owner_net" name="Net propriétaire" fill="#7c3aed" radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Contrats actifs */}
              {financial.active_contracts.length > 0 && (
                <div className="card" style={{ marginBottom: 24 }}>
                  <div className="card-header"><h2>Contrats de bail actifs</h2></div>
                  <div className="table-responsive">
                    <table className="table">
                      <thead><tr>
                        <th>N° Contrat</th><th>Locataire</th><th>Unité</th>
                        <th>Loyer mensuel</th><th>Début</th><th>Fin</th>
                      </tr></thead>
                      <tbody>
                        {financial.active_contracts.map(c => (
                          <tr key={c.id}>
                            <td><code style={{ fontSize: 12 }}>{c.contract_number || c.id.slice(0,8)}</code></td>
                            <td>{c.tenant_first_name} {c.tenant_last_name}</td>
                            <td>{c.unit_number ? `${c.unit_type || ''} ${c.unit_number}` : '—'}</td>
                            <td style={{ fontWeight: 600 }}>{fmt(c.monthly_rent)} XOF</td>
                            <td>{fmtDate(c.start_date)}</td>
                            <td>{fmtDate(c.end_date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Paiements récents */}
              {financial.recent_payments.length > 0 && (
                <div className="card">
                  <div className="card-header"><h2>Historique des paiements</h2></div>
                  <div className="table-responsive">
                    <table className="table">
                      <thead><tr>
                        <th>Échéance</th><th>Locataire</th><th>Unité</th>
                        <th>Montant</th><th>Méthode</th><th>Statut</th>
                      </tr></thead>
                      <tbody>
                        {financial.recent_payments.map(p => {
                          const sc = STATUS_PAYMENT[p.status] || { label: p.status, cls: 'badge-secondary' };
                          return (
                            <tr key={p.id}>
                              <td>{fmtDate(p.due_date)}</td>
                              <td>{p.tenant_first_name} {p.tenant_last_name}</td>
                              <td>{p.unit_number || '—'}</td>
                              <td style={{ fontWeight: 600 }}>{fmt(p.amount)} XOF</td>
                              <td>{p.payment_method_name || '—'}</td>
                              <td><span className={`badge ${sc.cls}`}>{sc.label}</span></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MandateDetailPage;
